import React from "react";
import Footer from "./Footer";
import Navbar from "./Navbar";
import SEO from "../component/SEO";
import { FaBalanceScale, FaHandshake, FaUserShield, FaRegStar, FaFileContract } from "react-icons/fa";

const FairPracticeCode: React.FC = () => {
  const policySections = [
    {
      title: "Our Ethical Foundation",
      icon: <FaHandshake />,
      content: `${import.meta.env.VITE_COMPANY_NAME} is committed to the highest standards of fair business and corporate practices. Our Fair Practice Code ensures that every applicant is treated with dignity, transparency, and complete equity, regardless of background or status.`,
    },
    {
      title: "1. Transparent Applications",
      icon: <FaFileContract />,
      list: [
        "Clear Communication: All forms and documents are provided in the language you understand.",
        "Informed Choice: We provide all necessary information so you can make financial decisions with confidence.",
        "Rapid Feedback: We acknowledge every application and provide clear timelines for resolution.",
      ],
    },
    {
      title: "2. Fair Appraisals",
      icon: <FaUserShield />,
      list: [
        "Explicit Terms: Loan amount, interest rates, and all charges are communicated clearly in writing.",
        "Documentation Access: You will always receive a signed copy of your loan agreement at the time of disbursal.",
        "Prospective Changes: Any updates to our terms are communicated in advance and applied only to future transactions.",
      ],
    },
    {
      title: "3. Responsible Recovery",
      icon: <FaBalanceScale />,
      list: [
        "Dignity & Respect: Our recovery practices are conducted with professionalism and zero harassment.",
        "Non-Interference: We respect your privacy and avoid unnecessary interference in your personal affairs.",
        "Fair Closures: Securities are released immediately upon repayment, ensuring your financial freedom.",
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-white text-text-primary px-0 mx-0 overflow-x-hidden font-sans selection:bg-secondary/10 selection:text-secondary">
      <SEO
        title="Fair Practice Code"
        description={`Our commitment to ethical and transparent lending. Learn about the Fair Practice Code at ${import.meta.env.VITE_COMPANY_NAME}.`}
        keywords={`fair practice code, ethical lending, fintech transparency, loan policies`}
      />
      
      <div className="fixed inset-0 pointer-events-none z-0 bg-gradient-to-b from-white via-gray-50/30 to-white" />
      
      <div className="relative z-50">
        <Navbar />
      </div>

      <main className="relative z-10 pt-44 pb-20 md:pb-24">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          
          <header className="mb-20">
             <div className="flex items-center gap-3 px-6 py-2 bg-white border border-secondary/20 shadow-xl w-fit rounded-full mb-10 transition-transform hover:scale-105">
                <FaRegStar className="text-secondary text-[10px]" />
                <span className="text-[10px] font-black tracking-[0.3em] text-text-secondary uppercase">The Epoch Promise</span>
              </div>
            <h1 className="text-5xl md:text-8xl font-black tracking-tighter mb-10 text-text-primary leading-[0.9]">
              Ethical <br />
              <span className="text-secondary">Commitment.</span>
            </h1>
            <p className="text-text-secondary text-lg md:text-xl font-medium leading-relaxed max-w-2xl">
              We stand for more than just lending. Our Fair Practice Code is our pledge to maintain absolute integrity, fairness, and transparency in every interaction.
            </p>
          </header>

          <div className="space-y-12">
            {policySections.map((section, idx) => (
              <div key={idx} className="bg-white p-10 md:p-14 rounded-[3rem] border border-gray-100 shadow-xl relative overflow-hidden transition-all hover:shadow-2xl">
                <div className="flex flex-col md:flex-row gap-8">
                  <div className="w-16 h-16 bg-gray-50 rounded-2xl flex items-center justify-center text-primary text-3xl shrink-0">
                    {section.icon}
                  </div>
                  <div className="flex-1">
                    <h2 className="text-2xl md:text-4xl font-black mb-6 text-text-primary tracking-tight">
                      {section.title}
                    </h2>
                    {section.content && (
                      <p className="text-text-secondary text-lg md:text-xl leading-relaxed font-medium mb-6">
                        {section.content}
                      </p>
                    )}
                    {section.list && (
                      <ul className="space-y-4 pt-4">
                        {section.list.map((li, i) => (
                          <li key={i} className="flex items-start gap-4 text-text-secondary font-medium text-lg leading-relaxed">
                            <div className="w-2 h-2 bg-secondary mt-2.5 rounded-full shrink-0" />
                            <span>{li}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </div>
            ))}
            
            {/* Regulatory Footer */}
            <div className="bg-primary text-white p-12 md:p-20 rounded-[3rem] shadow-2xl relative overflow-hidden">
                <div className="absolute top-0 right-0 w-80 h-80 bg-white/5 rounded-full blur-[80px]" />
                <h3 className="text-2xl md:text-4xl font-black mb-10 tracking-tighter">Institutional Integrity</h3>
                <p className="text-white/80 text-lg md:text-xl leading-relaxed font-medium mb-12">
                  Our Code is built on the RBI Guidelines (Notification No. DNBS (PD) CC No. 80 / 03.10.042 / 2005-06). We ensure that every employee at {import.meta.env.VITE_COMPANY_NAME} is trained to uphold these values daily.
                </p>
                <div className="flex flex-wrap gap-8 pt-8 border-t border-white/20">
                    <div className="text-xs font-black uppercase tracking-widest text-secondary">Board Approved: April 2025</div>
                    <div className="text-xs font-black uppercase tracking-widest text-secondary">RBI Compliant Operations</div>
                </div>
            </div>
          </div>

        </div>
      </main>

      <Footer />
    </div>
  );
};

export default FairPracticeCode;
